import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { api, getToken, clearToken } from './api';
import Login from './components/Login';
import AppShell from './components/AppShell';
import Departments from './components/Departments';
import Roles from './components/Roles';
import Users from './components/Users';
import Campaigns from './components/Campaigns';
import CampaignDetail from './components/CampaignDetail';
import MyTasks from './components/MyTasks';
import Brands from './components/Brands';
import UserAccess from './components/UserAccess';
import Permissions from './components/Permissions';
import Store from './components/Store';
import Requirements, { RequirementDetail } from './components/Requirements';
import PurchaseRequests from './components/PurchaseRequests';
import Warranty, { WarrantyDetail } from './components/Warranty';
import WarrantyProducts from './components/WarrantyProducts';
import AuditLog from './components/AuditLog';
import Assembly from './components/Assembly';
import Replenishment from './components/Replenishment';
import InventoryOverview from './components/InventoryOverview';
import HomeGrid from './components/HomeGrid';
import './odoo.css';
import { Icon } from './icons';

// Shared context: the logged-in user + their viewable module tree (from /api/me).
const MeContext = createContext(null);
export const useMe = () => useContext(MeContext);

function RequireAuth({ children }) {
  const [state, setState] = useState({ loading: true, me: null });

  useEffect(() => {
    if (!getToken()) { setState({ loading: false, me: null }); return; }
    api('/me')
      .then((me) => setState({ loading: false, me }))
      .catch(() => { clearToken(); setState({ loading: false, me: null }); });
  }, []);

  if (state.loading) return <div className="center-load">Loading…</div>;
  if (!state.me) return <Navigate to="/login" replace />;

  return <MeContext.Provider value={state.me}>{children}</MeContext.Provider>;
}

function Dashboard() {
  const me = useMe();
  const children = me.modules.flatMap((m) => m.children);
  return (
    <div className="page-card" style={{ maxWidth: 'none', background: 'transparent', border: 'none', padding: 0 }}>
      <div className="page-eyebrow">Overview</div>
      <h1 className="page-title">Welcome back, {me.user.name.split(' ')[0]}</h1>
      <p className="page-desc">You have access to {me.modules.length} module groups. Choose an area from the sidebar to get started.</p>
      <div className="dash-grid">
        {children.map((c) => (
          <a key={c.key} className="dash-tile" href={'/m/' + c.key}>
            <div className="t-icon"><Icon name={c.icon} /></div>
            <div>
              <div className="t-label">{c.label}</div>
              <div className="t-sub">Open {c.label.toLowerCase()}</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function ModulePage() {
  const me = useMe();
  const { key } = useParams();

  // Real screens, keyed by module. Others show a placeholder for now.
  const REAL = {
    departments: Departments,
    roles: Roles,
    users: Users,
    campaigns: Campaigns,
    my_tasks: MyTasks,
    brands: Brands,
    user_access: UserAccess,
    permissions: Permissions,
    store: Store,
    requirements: Requirements,
    purchase_requests: PurchaseRequests,
    warranty_applications: Warranty,
    warranty_products: WarrantyProducts,
    audit_log: AuditLog,
    assembly: Assembly,
    replenishment: Replenishment,
    inventory_overview: InventoryOverview,
  };
  const Real = REAL[key];
  if (Real) return <Real />;

  const all = me.modules.flatMap((m) => [m, ...m.children]);
  const mod = all.find((m) => m.key === key);
  const label = mod ? mod.label : 'Not found';
  return (
    <div className="page-card">
      <div className="page-eyebrow">{mod && mod.parent_id ? 'Module' : 'Section'}</div>
      <h1 className="page-title">{label}</h1>
      <p className="page-desc">
        This is the {label} area. The full screen for managing {label.toLowerCase()} is being built next.
      </p>
      <span className="soon-tag">Coming soon</span>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<RequireAuth><AppShell /></RequireAuth>}>
          <Route path="/" element={<HomeGrid />} />
          <Route path="/m/campaigns/:id" element={<CampaignDetail />} />
          <Route path="/m/requirements/:id" element={<RequirementDetail />} />
          <Route path="/m/warranty_applications/:id" element={<WarrantyDetail />} />
          <Route path="/m/:key" element={<ModulePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
